/**
 * Menu de navegacion lateral.
 *
 * Organizado en grupos con encabezados discretos para que el PL ubique al
 * vuelo dónde hacer cada cosa. La sección "Configuración" queda al fondo
 * (push-to-bottom) porque son funciones del ADMIN_PMIS — el PL/PL2 no las
 * toca a diario.
 *
 * Filtros aplicados al render:
 *   - `tecpetrolOnly`: oculta el item a contratistas e interventores.
 *   - `perm`: oculta el item si el rol del usuario no lo tiene.
 *   - Grupos vacíos (todos los items filtrados) no se renderizan ni dejan
 *     un encabezado huérfano.
 */

import { NavLink } from "react-router-dom";
import { usePermissions } from "@core/auth/usePermissions";
import { useAuth } from "@core/auth/useAuth";
import { esContratista, esInterventor } from "@core/auth/scope";
import { BENEFICIO_VIEWERS } from "@constants/accesoEspecial";
import {
  HomeRegular,
  MoneyRegular,
  ClipboardRegular,
  CalendarRegular,
  WarningRegular,
  PeopleRegular,
  DocumentRegular,
  HandshakeRegular,
  CartRegular,
  ReceiptRegular,
  BoxRegular,
  CalendarLtrRegular,
  TableRegular,
  MailRegular,
  SparkleRegular,
  ShieldErrorRegular,
  PeopleTeamRegular,
  SettingsRegular,
  EarthRegular,
  ClipboardTaskRegular,
  DataPieRegular,
  LightbulbRegular,
} from "@fluentui/react-icons";
import type { ReactNode } from "react";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  perm: string;
  /** Si true, solo activo en match exacto. */
  end?: boolean;
  /**
   * Si true, el módulo solo es visible para personal de Tecpetrol; se oculta
   * al contratista (PLANNER/ADOR) y a los interventores (INT).
   */
  tecpetrolOnly?: boolean;
  /**
   * Allowlist de emails específicos que ven el item, además del ADMIN_PMIS.
   * Si está presente, NO se aplica el chequeo `perm` ni `tecpetrolOnly` —
   * solo ADMIN_PMIS y los emails listados ven el item.
   *
   * Uso: módulos restringidos a un set muy pequeño de stakeholders (ej.
   * Beneficio ejecutivo a ADMIN_PMIS + Rafael García).
   */
  allowlistEmails?: Set<string>;
}

interface NavGroup {
  /** Si undefined, no se renderiza encabezado (item suelto, como "Inicio"). */
  label?: string;
  /**
   * Si true, este grupo se empuja al fondo del sidebar usando `marginTop: auto`.
   * Solo debe haber UN grupo con esta bandera.
   */
  pushToBottom?: boolean;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  // ── Inicio (item suelto, sin encabezado) ──────────────────────────
  {
    items: [
      // Inicio expone KPIs financieros del proyecto — solo personal Tecpetrol.
      { to: "/", label: "Inicio", icon: <HomeRegular />, perm: "", tecpetrolOnly: true },
    ],
  },

  // ── Núcleo del proyecto: lo que el PL/PL2/Interventor usan a diario ──
  {
    label: "Núcleo del proyecto",
    items: [
      { to: "/budget", label: "Budget", icon: <CalendarLtrRegular />, perm: "budget:view" },
      { to: "/afe", label: "AFEs", icon: <MoneyRegular />, perm: "afe:view" },
      { to: "/so", label: "Órdenes de Trabajo", icon: <ClipboardRegular />, perm: "so:view" },
      { to: "/rd", label: "Reportes Diarios", icon: <CalendarRegular />, perm: "rd:view" },
      {
        to: "/recursos",
        label: "Registro de recursos",
        icon: <PeopleTeamRegular />,
        perm: "recursos:view",
      },
      { to: "/actas", label: "Certificaciones", icon: <DocumentRegular />, perm: "act:view" },
    ],
  },

  // ── Procurement: cadena de compras ────────────────────────────────
  {
    label: "Procurement",
    items: [
      { to: "/pr", label: "Purchase Requisitions", icon: <CartRegular />, perm: "pr:view" },
      { to: "/po", label: "Purchase Orders", icon: <ReceiptRegular />, perm: "po:view" },
      { to: "/mr", label: "Materiales por CM", icon: <BoxRegular />, perm: "mr:view" },
      { to: "/gastos-sap", label: "Gastos SAP (AC)", icon: <DataPieRegular />, perm: "afe:view" },
      {
        to: "/contratos",
        label: "Contratos Marco",
        icon: <HandshakeRegular />,
        perm: "contract:view",
      },
    ],
  },

  // ── Gestión PMI: riesgos, stakeholders, eventos ──────────────────
  // PMI PMBOK 7 / IPMA ICB4 / ISO 31000 / SAFe 6.0.
  // Detalle en docs/ALCANCE_RIESGOS_STAKEHOLDERS.md.
  {
    label: "Gestión PMI",
    items: [
      { to: "/eventos", label: "Eventos", icon: <WarningRegular />, perm: "event:view" },
      {
        to: "/riesgos",
        label: "Riesgos",
        icon: <ShieldErrorRegular />,
        perm: "risk:view",
        // Riesgos del proyecto contiene info estratégica y sensible — solo
        // personal Tecpetrol. Contratistas e interventores no lo ven.
        tecpetrolOnly: true,
      },
      {
        to: "/stakeholders",
        label: "Stakeholders",
        icon: <PeopleTeamRegular />,
        perm: "stakeholder:view",
        // Contratistas no ven a otros stakeholders.
        tecpetrolOnly: true,
      },
      // Comisionamiento (punch list) — visible para Tecpetrol + interventoría
      // + contratistas (los contratistas pueden ver pendientes de sus SOs
      // para coordinar el cierre). El permiso `comm:view` ya está abierto.
      {
        to: "/comisionamiento",
        label: "Comisionamiento",
        icon: <ClipboardTaskRegular />,
        perm: "comm:view",
      },
    ],
  },

  // ── Configuración y Maestros: mezcla datos base poco-modificados
  // (Autoridades, Catálogo Costes) con configuración del sistema (Notif., IA,
  // Riesgos). Se muestran desplegados a continuación de Gestión PMI.
  {
    label: "Configuración y Maestros",
    items: [
      // Maestros — datos de referencia que cambian rara vez.
      { to: "/autoridades", label: "Autoridades", icon: <PeopleRegular />, perm: "users:view" },
      { to: "/costes", label: "Catálogo Costes", icon: <TableRegular />, perm: "costes:view" },
      {
        to: "/configuracion/gerencias",
        label: "Gerencias / Macroproyectos",
        icon: <EarthRegular />,
        perm: "notif:admin",
      },
      // Beneficio — reporte ejecutivo restringido (ADMIN_PMIS + allowlist).
      // No usa el sistema de permisos por rol; chequea email individual.
      {
        to: "/beneficio",
        label: "Beneficio",
        icon: <DataPieRegular />,
        perm: "",
        allowlistEmails: BENEFICIO_VIEWERS,
      },
      // Gestión de reportes — qué PDFs/impresiones están autorizados a
      // generarse. EXCLUSIVO del ADMIN_PMIS. Se gestiona por rol activo:
      // Rafael solo lo ve cuando ingresa eligiendo ADMIN_PMIS, no como PL2.
      {
        to: "/configuracion/reportes",
        label: "Gestión de reportes",
        icon: <DocumentRegular />,
        perm: "reportes:admin",
      },
      // Configuración del sistema — solo ADMIN_PMIS.
      {
        to: "/configuracion/notificaciones",
        label: "Notificaciones",
        icon: <MailRegular />,
        perm: "notif:admin",
      },
      {
        to: "/configuracion/distribucion",
        label: "Lista distribución",
        icon: <PeopleTeamRegular />,
        perm: "notif:admin",
      },
      {
        to: "/configuracion/ia",
        label: "Asistencia IA",
        icon: <SparkleRegular />,
        perm: "ia:admin",
      },
      {
        to: "/configuracion/riesgos",
        label: "Catálogo Riesgos",
        icon: <SettingsRegular />,
        perm: "risk:configure",
      },
    ],
  },

  // ── Mejora continua: feedback del propio PMIS ────────────────────
  // Visible para TODOS los roles (cualquier usuario puede reportar
  // hallazgos / observaciones / recomendaciones que encuentre al usar
  // el sistema). Sin restricción por gerencia — es un meta-módulo.
  // Solo el ADMIN_PMIS puede responder y cambiar estado.
  {
    label: "Mejora continua",
    items: [
      {
        to: "/mejoras",
        label: "Mejoras PMIS",
        icon: <LightbulbRegular />,
        perm: "mejoras:view",
      },
    ],
  },
];

interface NavMenuProps {
  /**
   * Modo de presentación:
   *   - "sidebar" (default): fijo a la izquierda con ancho 220px. Para
   *     desktop/tablet horizontal.
   *   - "drawer": overlay que entra desde la izquierda. Para mobile/tablet
   *     vertical. Controlado por `open` + `onClose`.
   */
  mode?: "sidebar" | "drawer";
  open?: boolean;
  onClose?: () => void;
}

export function NavMenu({ mode = "sidebar", open = false, onClose }: NavMenuProps) {
  const { can, role } = usePermissions();
  const { user } = useAuth();
  const esExterno = esContratista(role) || esInterventor(role);
  const emailActual = (user?.email ?? "").toLowerCase();

  // Filtramos items por permiso/rol/allowlist y dejamos solo grupos con items.
  const gruposVisibles = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((item) => {
      // Allowlist específica (Beneficio / Gestión Reportes): solo el
      // ADMIN_PMIS o emails listados pueden verlo. Ignora `perm` y
      // `tecpetrolOnly` porque la allowlist es más restrictiva.
      if (item.allowlistEmails) {
        if (role === "ADMIN_PMIS") return true;
        return item.allowlistEmails.has(emailActual);
      }
      if (item.tecpetrolOnly && esExterno) return false;
      return item.perm === "" || can(item.perm);
    }),
  })).filter((g) => g.items.length > 0);

  // ── Modo drawer (mobile): overlay con backdrop ──
  if (mode === "drawer") {
    return (
      <>
        {open && (
          <div
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              zIndex: 998,
              animation: "fadeIn 150ms",
            }}
            aria-hidden="true"
          />
        )}
        <nav
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            bottom: 0,
            width: 260,
            background: "#f5f5f5",
            borderRight: "1px solid #e0e0e0",
            padding: "1rem 0.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.25rem",
            zIndex: 999,
            boxShadow: open ? "2px 0 16px rgba(0,0,0,0.18)" : "none",
            transform: open ? "translateX(0)" : "translateX(-100%)",
            transition: "transform 200ms ease",
            overflowY: "auto",
          }}
          aria-hidden={!open}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 0.5rem 0.75rem 0.5rem",
              marginBottom: 4,
              borderBottom: "1px solid #ddd",
            }}
          >
            <strong style={{ color: "#003A70", fontSize: 14 }}>Navegación</strong>
            <button
              onClick={onClose}
              aria-label="Cerrar menú"
              style={{
                background: "transparent",
                border: "none",
                fontSize: 20,
                cursor: "pointer",
                color: "#666",
                padding: "4px 8px",
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
          {renderGrupos(gruposVisibles, "drawer", onClose)}
        </nav>
      </>
    );
  }

  // ── Modo sidebar (desktop): fijo en flujo normal ──
  return (
    <nav
      style={{
        width: 220,
        background: "#f5f5f5",
        borderRight: "1px solid #e0e0e0",
        padding: "1rem 0.5rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.25rem",
        minHeight: "calc(100vh - 60px)",
      }}
    >
      {renderGrupos(gruposVisibles, "sidebar")}
    </nav>
  );
}

// ── Render compartido entre sidebar y drawer ─────────────────────────

function renderGrupos(
  grupos: NavGroup[],
  modo: "sidebar" | "drawer",
  onClose?: () => void,
): ReactNode {
  return grupos.map((g, idx) => (
    <div
      key={g.label ?? `grupo-${idx}`}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.2rem",
        // El grupo marcado como pushToBottom se separa con margen automático
        // — solo aplica en sidebar, donde el contenedor tiene altura completa.
        ...(g.pushToBottom && modo === "sidebar" ? { marginTop: "auto" } : {}),
        // En drawer mobile, dejamos un margen extra arriba para diferenciar
        // visualmente la sección configuración.
        ...(g.pushToBottom && modo === "drawer" ? { marginTop: "1.5rem" } : {}),
      }}
    >
      {g.label && (
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.6,
            color: "#6B7280",
            textTransform: "uppercase",
            padding: "8px 12px 4px 12px",
            // Separador sutil arriba — excepto si es el primer grupo del nav
            // o si es el grupo pushToBottom (ya tiene margen propio).
            borderTop: g.pushToBottom ? "1px solid #d8dde3" : "none",
            paddingTop: g.pushToBottom ? 14 : 8,
            marginTop: g.pushToBottom ? 4 : 0,
          }}
        >
          {g.label}
        </div>
      )}
      {g.items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end ?? item.to === "/"}
          onClick={onClose}
          style={({ isActive }) => ({
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            padding: modo === "drawer" ? "0.75rem 0.75rem" : "0.5rem 0.75rem",
            borderRadius: 6,
            textDecoration: "none",
            color: isActive ? "#003A70" : "#333",
            background: isActive ? "#dde8f4" : "transparent",
            fontWeight: isActive ? 600 : 400,
            fontSize: 14,
            ...(modo === "drawer" ? { minHeight: 44 } : {}),
          })}
        >
          <span style={{ fontSize: 18, display: "inline-flex", alignItems: "center" }}>
            {item.icon}
          </span>
          {item.label}
        </NavLink>
      ))}
    </div>
  ));
}
